import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ButtonGroup
} from '@mui/material';
import {
  Visibility as PreviewIcon,
  Code as CodeIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  ArrowDownward as ArrowIcon,
  RadioButtonUnchecked as CircleIcon,
  CropSquare as SquareIcon,
  ChangeHistory as DiamondIcon
} from '@mui/icons-material';
import mermaid from 'mermaid';

// Initialize Mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis'
  }
});

const MermaidEditor = ({
  initialCode = '',
  initialTitle = '',
  onSave,
  onCancel,
  onCodeChange,
  height = '70vh',
  showToolbar = true,
  showTitleField = false,
  showCodeActions = true
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [activeDiagramType, setActiveDiagramType] = useState('flowchart');
  const [mermaidCode, setMermaidCode] = useState(initialCode);
  const [diagramTitle, setDiagramTitle] = useState(initialTitle);
  const [renderedSvg, setRenderedSvg] = useState('');
  const [error, setError] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [selectedNodeType, setSelectedNodeType] = useState('process');
  const previewRef = useRef(null);
  const editorId = useRef(`mermaid-editor-${Date.now()}`);

  // Mermaid templates for quick insertion
  const templates = {
    flowchart: `flowchart TD
    A[Start] --> B{Decision?}
    B -->|Yes| C[Process 1]
    B -->|No| D[Process 2]
    C --> E[End]
    D --> E`,

    sequence: `sequenceDiagram
    participant A as User
    participant B as System
    A->>B: Request
    B-->>A: Response`,

    classDiagram: `classDiagram
    class User {
        +String name
        +login()
    }`,

    stateDiagram: `stateDiagram-v2
    [*] --> Idle
    Idle --> Processing
    Processing --> Complete
    Complete --> [*]`,

    erDiagram: `erDiagram
    USER ||--o{ ORDER : places
    ORDER ||--|{ LINE-ITEM : contains`
  };

  // Node shape helpers
  const nodeShapes = {
    rectangle: (id, text) => `${id}[${text}]`,
    circle: (id, text) => `${id}((${text}))`,
    diamond: (id, text) => `${id}{${text}}`,
    hexagon: (id, text) => `${id}{{${text}}}`,
    parallelogram: (id, text) => `${id}[/${text}/]`,
    trapezoid: (id, text) => `${id}[\\${text}\\]`
  };

  // Render Mermaid diagram
  const renderMermaid = useCallback(async () => {
    if (!mermaidCode.trim()) {
      setRenderedSvg('');
      setError('');
      return;
    }

    try {
      setError('');
      const svgId = `${editorId.current}-${Date.now()}`;

      // Validate and render
      const { svg } = await mermaid.render(svgId, mermaidCode);
      setRenderedSvg(svg);
    } catch (err) {
      console.error('Mermaid render error:', err);
      setError(err.message || 'Failed to render diagram');
      setRenderedSvg('');
    }
  }, [mermaidCode]);

  // Auto-render when code changes (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      renderMermaid();
    }, 500);

    return () => clearTimeout(timer);
  }, [mermaidCode, renderMermaid]);

  // Initial render
  useEffect(() => {
    if (initialCode) {
      setMermaidCode(initialCode);
    }
  }, [initialCode]);

  // Update title when initialTitle changes
  useEffect(() => {
    if (initialTitle) {
      setDiagramTitle(initialTitle);
    }
  }, [initialTitle]);

  // Push code changes to parent (used by ProcessFlowVisualEditor when this
  // component is rendered as the "advanced" view). No-op when caller doesn't
  // pass onCodeChange.
  useEffect(() => {
    if (onCodeChange) onCodeChange(mermaidCode);
  }, [mermaidCode, onCodeChange]);

  // Handle zoom
  const handleZoom = (direction) => {
    setZoomLevel(prev => {
      const newLevel = direction === 'in' ?
        Math.min(prev + 0.1, 2.0) :
        Math.max(prev - 0.1, 0.5);
      return newLevel;
    });
  };

  // Add node to current cursor position
  const addNode = (shape) => {
    const nodeId = `node${Date.now()}`;
    const nodeText = `New ${shape}`;
    const nodeCode = nodeShapes[shape](nodeId, nodeText);

    // Find cursor position and insert
    const textarea = document.querySelector('.mermaid-code-editor');
    if (textarea) {
      const cursorPos = textarea.selectionStart;
      const textBefore = mermaidCode.substring(0, cursorPos);
      const textAfter = mermaidCode.substring(cursorPos);

      // Add new line if needed
      const newLine = textBefore.endsWith('\n') ? '' : '\n';
      setMermaidCode(textBefore + newLine + nodeCode + '\n' + textAfter);
    } else {
      setMermaidCode(prev => prev + '\n' + nodeCode);
    }
  };

  // Insert template
  const insertTemplate = (templateType) => {
    setMermaidCode(templates[templateType]);
    setActiveDiagramType(templateType);
    setActiveTab(0); // Switch to code tab
  };

  // Handle save
  const handleSave = () => {
    if (onSave) {
      // Pass both code and title if title field is shown
      if (showTitleField) {
        onSave(mermaidCode, diagramTitle);
      } else {
        onSave(mermaidCode);
      }
    }
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(mermaidCode);
  };

  // Download as text
  const downloadCode = () => {
    const blob = new Blob([mermaidCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'diagram.mmd';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ height, display: 'flex', flexDirection: 'column', px: 4, py: 1 }}>
      {/* Title Field */}
      {showTitleField && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
            Diagram Title
          </Typography>
          <TextField
            value={diagramTitle}
            onChange={(e) => setDiagramTitle(e.target.value)}
            fullWidth
            placeholder="Enter diagram title"
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                color: 'var(--color-blue, #475969)',
                height: '45px',
                transition: 'all 0.2s',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#475969',
                  borderWidth: '1px',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#475969',
                  borderWidth: '1px',
                }
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4759694d',
                border: '1px solid #4759694d',
                borderRadius: '4px',
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
                padding: '12px 14px',
                color: 'var(--color-blue, #475969)',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#a2abb3',
                opacity: 0.7,
                fontSize: '14px',
              },
            }}
          />
        </Box>
      )}
      {/* Toolbar */}
      {showToolbar && (
        <Paper elevation={0} sx={{ display: 'flex', width: '100%', margin: '10px 0 !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            {/* Diagram Type Tabs */}
            <Box sx={{ display: 'flex', gap: 0, width: '100%', display: 'flex' }}>
              <Box
                onClick={() => insertTemplate('flowchart')}
                sx={{
                  px: 3,
                  py: 1.5,
                  cursor: 'pointer',
                  borderBottom: 3,
                  borderColor: activeDiagramType === 'flowchart' ? '#475969' : 'transparent',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: activeDiagramType === 'flowchart' ? '#475969' : '#475969',
                  bgcolor: activeDiagramType === 'flowchart' ? '#47596940' : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: activeDiagramType === 'flowchart' ? '#47596940' : 'rgba(71, 89, 105, 0.04)'
                  },
                  display: 'flex',
                  flex: '1 1',
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                Flowchart
              </Box>
              <Box
                onClick={() => insertTemplate('sequence')}
                sx={{
                  px: 3,
                  py: 1.5,
                  cursor: 'pointer',
                  borderBottom: 3,
                  borderColor: activeDiagramType === 'sequence' ? '#475969' : 'transparent',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: activeDiagramType === 'sequence' ? '#475969' : '#475969',
                  bgcolor: activeDiagramType === 'sequence' ? '#47596940' : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: activeDiagramType === 'sequence' ? '#47596940' : 'rgba(71, 89, 105, 0.04)'
                  },
                  display: 'flex',
                  flex: '1 1',
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                Sequence
              </Box>
              <Box
                onClick={() => insertTemplate('classDiagram')}
                sx={{
                  px: 3,
                  py: 1.5,
                  cursor: 'pointer',
                  borderBottom: 3,
                  borderColor: activeDiagramType === 'classDiagram' ? '#475969' : 'transparent',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: activeDiagramType === 'classDiagram' ? '#475969' : '#475969',
                  bgcolor: activeDiagramType === 'classDiagram' ? '#47596940' : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: activeDiagramType === 'classDiagram' ? '#47596940' : 'rgba(71, 89, 105, 0.04)'
                  },
                  display: 'flex',
                  flex: '1 1',
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}

              >
                Class
              </Box>
              <Box
                onClick={() => insertTemplate('stateDiagram')}
                sx={{
                  px: 3,
                  py: 1.5,
                  cursor: 'pointer',
                  borderBottom: 3,
                  borderColor: activeDiagramType === 'stateDiagram' ? '#475969' : 'transparent',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: activeDiagramType === 'stateDiagram' ? '#475969' : '#475969',
                  bgcolor: activeDiagramType === 'stateDiagram' ? '#47596940' : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: activeDiagramType === 'stateDiagram' ? '#47596940' : 'rgba(71, 89, 105, 0.04)'
                  },
                  display: 'flex',
                  flex: '1 1',
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                State
              </Box>
            </Box>

            {/* Add Node Section - Tab-like Style */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Select
                value={selectedNodeType}
                onChange={(e) => setSelectedNodeType(e.target.value)}
                size="small"
                displayEmpty
                sx={{
                  minWidth: 120,
                  height: 36,
                  fontSize: '13px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#475969'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#475969',
                    borderWidth: '1px'
                  }
                }}
              >
                <MenuItem value="rectangle">Rectangle</MenuItem>
                <MenuItem value="circle">Circle</MenuItem>
                <MenuItem value="diamond">Diamond</MenuItem>
                <MenuItem value="hexagon">Hexagon</MenuItem>
              </Select>

              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => addNode(selectedNodeType)}

              >
                Add
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Tabs */}
      <Paper elevation={1} sx={{ flex: 1, display: 'flex', flexDirection: 'column', margin: '0px !important', minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ marginBottom: '10px !important' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              minHeight: 'unset',
              '& .MuiTabs-flexContainer': {
                minHeight: 'unset'
              }
            }}
          >
            <Tab
              label="Code Editor"
              icon={<CodeIcon />}
              iconPosition="start"
              sx={{ minHeight: 'unset' }}
            />
            <Tab
              label="Preview"
              icon={<PreviewIcon />}
              iconPosition="start"
              sx={{ minHeight: 'unset' }}
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0 }}>
          {/* Code Editor Tab */}
          {activeTab === 0 && (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {showCodeActions && (
                <Box sx={{ py: 1, display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={renderMermaid}
                  >
                    Render
                  </Button>
                  <Button
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={copyToClipboard}
                  >
                    Copy
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={downloadCode}
                  >
                    Download
                  </Button>
                </Box>
              )}

              <TextField
                className="mermaid-code-editor"
                multiline
                fullWidth
                value={mermaidCode}
                onChange={(e) => setMermaidCode(e.target.value)}
                placeholder="Enter Mermaid diagram code here..."
                variant="outlined"
                sx={{
                  flex: 1,
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'flex-start'
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                    fontSize: '14px',
                    height: '100% !important',
                    overflow: 'auto !important',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-track': {
                      borderRadius: '10px',
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      marginTop: '5px',
                      marginBottom: '5px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      borderRadius: '10px',
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.4)',
                      },
                    },
                    '&::-webkit-scrollbar-corner': {
                      backgroundColor: 'transparent',
                    },
                  },
                  '& fieldset': {
                    border: 'none'
                  }
                }}
              />
            </Box>
          )}

          {/* Preview Tab */}
          {activeTab === 1 && (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box sx={{ py: 1, display: 'flex', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Tooltip title="Zoom In">
                  <IconButton size="small" onClick={() => handleZoom('in')}>
                    <ZoomInIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Zoom Out">
                  <IconButton size="small" onClick={() => handleZoom('out')}>
                    <ZoomOutIcon />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" sx={{ alignSelf: 'center', ml: 1 }}>
                  {Math.round(zoomLevel * 100)}%
                </Typography>
              </Box>

              <Box
                ref={previewRef}
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  py: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  backgroundColor: '#f9f9f9',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    backgroundColor: 'transparent',
                  },
                  '&::-webkit-scrollbar-track': {
                    borderRadius: '10px',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    marginTop: '5px',
                    marginBottom: '5px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    borderRadius: '10px',
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.4)',
                    },
                  },
                  '&::-webkit-scrollbar-corner': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {error ? (
                  <Alert severity="error" sx={{ maxWidth: '100%' }}>
                    <Typography variant="body2">
                      <strong>Mermaid Error:</strong><br />
                      {error}
                    </Typography>
                  </Alert>
                ) : renderedSvg ? (
                  <Box
                    sx={{
                      transform: `scale(${zoomLevel})`,
                      transformOrigin: 'top center',
                      maxWidth: '100%'
                    }}
                    dangerouslySetInnerHTML={{ __html: renderedSvg }}
                  />
                ) : (
                  <Typography color="text.secondary">
                    Enter Mermaid code to see preview
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Action Buttons */}
      {(onSave || onCancel) && (
        <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {onCancel && (
            <Button
              startIcon={<CancelIcon />}
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
          {onSave && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{
                textTransform: 'none !important'
              }}
            >
              Save changes
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MermaidEditor;